import type { HistogramBin } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";

interface SpreadHistPanelProps {
  data: HistogramBin[];
  loading?: boolean;
}

export function SpreadHistPanel({ data, loading }: SpreadHistPanelProps) {
  const cc = useChartColors();
  // Filter corrupt overflow bin, then sum counts across pools
  const clean = data.filter((d) => d.bin !== "> 100");
  const countMap = new Map<string, number>();
  for (const d of clean) {
    countMap.set(d.bin, (countMap.get(d.bin) ?? 0) + d.block_count);
  }

  const bins = [...countMap.keys()].sort();
  const counts = bins.map((b) => countMap.get(b) ?? 0);
  const total = counts.reduce((s, c) => s + c, 0);

  // cumulative % computed from counts (not summed per-pool pcts)
  const cumPcts: number[] = [];
  let cum = 0;
  for (const c of counts) {
    cum += total > 0 ? (c / total) * 100 : 0;
    cumPcts.push(cum);
  }

  const option = {
    backgroundColor: "transparent",
    grid: { top: 16, right: 60, bottom: 36, left: 50 },
    xAxis: {
      type: "category" as const,
      data: bins,
      axisLabel: { fontSize: 10, color: cc.text, rotate: 30 },
      axisLine: { lineStyle: { color: cc.grid } },
    },
    yAxis: [
      {
        type: "value" as const,
        name: "Blocks",
        nameTextStyle: { fontSize: 10, color: cc.text },
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        splitLine: { lineStyle: { color: cc.grid, type: "dashed" as const } },
      },
      {
        type: "value" as const,
        name: "Cumulative %",
        nameTextStyle: { fontSize: 10, color: cc.text },
        axisLabel: {
          fontSize: 10,
          color: cc.text,
          formatter: (v: number) => `${v.toFixed(0)}%`,
        },
        axisLine: { lineStyle: { color: cc.grid } },
        splitLine: { show: false },
        max: 100,
      },
    ],
    series: [
      {
        type: "bar" as const,
        data: counts,
        itemStyle: { color: "#38bdf8" },
        yAxisIndex: 0,
      },
      {
        type: "line" as const,
        data: cumPcts,
        smooth: true,
        lineStyle: { color: "#a855f7", width: 2 },
        itemStyle: { color: "#a855f7" },
        symbol: "none",
        yAxisIndex: 1,
      },
    ],
    tooltip: { trigger: "axis" as const },
  };

  const containerRef = useECharts(option, [data, cc.grid]);

  return (
    <Panel
      title="SPREAD HISTOGRAM"
      description="How often each spread size occurs across all blocks. Most blocks cluster near 0 bps. The purple line shows the cumulative share of blocks up to that spread size."
    >
      <div style={{ position: "relative", height: 200 }}>
        <div ref={containerRef} style={{ height: 200 }} />
        {loading && (
          <div className="skeleton" style={{ position: "absolute", inset: 0, borderRadius: 4 }} />
        )}
      </div>
    </Panel>
  );
}
